import {
  COMPANIES, COMPANY_ORDER, PERSON_ORDER, PERSONS, STATIC_FLAGS, STEPS,
  Company, Version, isPerson,
} from "./data";
import {
  Frac, ZERO, addF, mulF, makeFrac, greaterThanPercent, lessThanPercent, isZero, toPercentString,
} from "./rational";

// Versi aktif sebuah PT pada langkah tertentu = versi terakhir dengan step <= langkah aktif.
export function activeVersion(companyId: string, step: number): Version | null {
  const c = COMPANIES[companyId];
  if (!c) return null;
  let chosen: Version | null = null;
  for (const v of c.versions) {
    if (v.step <= step) {
      if (!chosen || v.step > chosen.step) chosen = v;
    }
  }
  return chosen;
}

export function companyExistsAt(companyId: string, step: number): boolean {
  return activeVersion(companyId, step) !== null;
}

// total lembar ditempatkan = modal ditempatkan / nominal (eksak, BigInt)
export function totalSharesBig(v: Version): bigint {
  return BigInt(v.ditempatkan) / BigInt(v.nominal);
}

export function directFrac(v: Version, holderId: string): Frac {
  let shares = 0;
  for (const [id, s] of v.holders) if (id === holderId) shares += s;
  if (shares === 0) return ZERO;
  return makeFrac(BigInt(shares), totalSharesBig(v));
}

// Total setoran modal LANGSUNG seseorang di seluruh PT pada langkah aktif
// = Σ (lembar dimiliki × nominal per lembar). Juga rinci per PT.
export function personDirectCapital(personId: string, step: number): { total: number; parts: { companyId: string; shares: number; amount: number }[] } {
  let total = 0;
  const parts: { companyId: string; shares: number; amount: number }[] = [];
  for (const cid of COMPANY_ORDER) {
    const v = activeVersion(cid, step);
    if (!v) continue;
    let shares = 0;
    for (const [id, s] of v.holders) if (id === personId) shares += s;
    if (shares > 0) { const amount = shares * v.nominal; total += amount; parts.push({ companyId: cid, shares, amount }); }
  }
  return { total, parts };
}

export interface EffCache {
  step: number;
  map: Map<string, Frac>;
  stack: Set<string>;
}

export function newEffCache(step: number): EffCache {
  return { step, map: new Map(), stack: new Set() };
}

// Kepemilikan efektif seseorang pada sebuah PT (rasional eksak, memoized, aman siklus).
export function effective(personId: string, companyId: string, cache: EffCache): Frac {
  const key = personId + "@" + companyId;
  const cached = cache.map.get(key);
  if (cached) return cached;
  if (cache.stack.has(key)) return ZERO; // pengaman siklus
  cache.stack.add(key);

  const v = activeVersion(companyId, cache.step);
  let sum: Frac = ZERO;
  if (v) {
    // gabungkan lembar per holder
    const byHolder = new Map<string, number>();
    for (const [id, s] of v.holders) byHolder.set(id, (byHolder.get(id) || 0) + s);
    const total = totalSharesBig(v);
    byHolder.forEach((shares, holderId) => {
      const frac = makeFrac(BigInt(shares), total);
      if (isPerson(holderId)) {
        if (holderId === personId) sum = addF(sum, frac);
      } else {
        sum = addF(sum, mulF(frac, effective(personId, holderId, cache)));
      }
    });
  }
  cache.stack.delete(key);
  cache.map.set(key, sum);
  return sum;
}

// Daftar orang yang punya kepemilikan efektif > 0 pada sebuah PT (urut personOrder)
export function personsWithEffective(companyId: string, cache: EffCache): { id: string; frac: Frac }[] {
  const out: { id: string; frac: Frac }[] = [];
  for (const pid of PERSON_ORDER) {
    const f = effective(pid, companyId, cache);
    if (!isZero(f)) out.push({ id: pid, frac: f });
  }
  return out;
}

export function isUBOCandidate(personId: string, cache: EffCache): boolean {
  for (const cid of COMPANY_ORDER) {
    if (!companyExistsAt(cid, cache.step)) continue;
    const f = effective(personId, cid, cache);
    if (greaterThanPercent(f, 25)) return true;
  }
  return false;
}

// ---------------- Flags ----------------
export interface Flag {
  sev: "red" | "yellow" | "info";
  code: string;
  companyId: string;
  message: string;
  source?: string;
}

export function computeFlags(step: number): Flag[] {
  const flags: Flag[] = [];
  for (const cid of COMPANY_ORDER) {
    const v = activeVersion(cid, step);
    if (!v) continue;
    const c = COMPANIES[cid];
    const total = totalSharesBig(v);
    let holderShares = 0;
    for (const [, s] of v.holders) holderShares += s;

    // lembar × nominal = modal ditempatkan
    if (BigInt(holderShares) * BigInt(v.nominal) !== BigInt(v.ditempatkan)) {
      flags.push({ sev: "red", code: "SAHAM_TDK_COCOK", companyId: cid,
        message: `Jumlah lembar × nominal (${holderShares} × ${v.nominal.toLocaleString("id-ID")}) tidak sama dengan modal ditempatkan (${v.ditempatkan.toLocaleString("id-ID")}).`,
        source: v.srcHold });
    }
    // modal ditempatkan >= 25% modal dasar
    if (BigInt(v.ditempatkan) * 4n < BigInt(v.dasar)) {
      flags.push({ sev: "red", code: "MODAL_KURANG_25", companyId: cid,
        message: `Modal ditempatkan (${v.ditempatkan.toLocaleString("id-ID")}) kurang dari 25% modal dasar (${v.dasar.toLocaleString("id-ID")}).`,
        source: v.srcHold });
    }
    // minimal 2 pemegang saham
    const holderIds = new Set(v.holders.map((h) => h[0]));
    if (holderIds.size < 2) {
      flags.push({ sev: "red", code: "PEMEGANG_KURANG", companyId: cid,
        message: "Perseroan memiliki kurang dari 2 pemegang saham.", source: v.srcHold });
    }
    // minimal satu direktur aktif
    const hasDirector = v.officers.some((o) => /direktur/i.test(o[1]));
    if (!hasDirector) {
      flags.push({ sev: "red", code: "TANPA_DIREKTUR", companyId: cid,
        message: "Tidak ada direktur aktif dalam susunan pengurus.", source: v.srcHold });
    }
    // info: tepat 2 pemegang saham dan salah satunya < 1%
    if (holderIds.size === 2) {
      const small = v.holders.find((h) => lessThanPercent(makeFrac(BigInt(h[1]), total), 1));
      if (small) {
        flags.push({ sev: "info", code: "PEMEGANG_MINORITAS", companyId: cid,
          message: `${c.short} memiliki dua pemegang saham dan salah satunya di bawah 1%.`, source: v.srcHold });
      }
    }
  }
  // flag tetap dari data
  for (const sf of STATIC_FLAGS) {
    if (sf.from <= step && companyExistsAt(sf.company, step)) {
      flags.push({ sev: sf.sev, code: sf.code, companyId: sf.company, message: sf.msg, source: sf.s });
    }
  }
  return flags;
}

export function flagCounts(flags: Flag[]): { red: number; yellow: number } {
  let red = 0, yellow = 0;
  for (const f of flags) {
    if (f.sev === "red") red++;
    else if (f.sev === "yellow") yellow++; // info = catatan, bukan isu yang perlu tinjau
  }
  return { red, yellow };
}

export function companyFlagLevel(flags: Flag[], companyId: string): "red" | "yellow" | null {
  let has: "red" | "yellow" | null = null;
  for (const f of flags) {
    if (f.companyId !== companyId) continue;
    if (f.sev === "red") return "red";
    if (f.sev === "yellow") has = "yellow"; // info tidak mengubah status PT
  }
  return has;
}

// ---------------- Layout ----------------
export interface NoteLine {
  text: string;
  kind: "langsung" | "efektif";
}
export interface LaidNode {
  id: string;
  kind: "person" | "company";
  level: number;
  x: number; // kiri-atas
  y: number;
  w: number;
  h: number;          // tinggi kartu (tanpa catatan)
  noteH: number;      // tinggi area catatan di bawah kartu
  notes: NoteLine[];
  version?: Version;
  capital?: number;   // setoran modal langsung total (orang), pada langkah aktif
  holdings?: { companyId: string; shares: number; amount: number }[]; // rincian per PT (orang)
  badgeRow?: boolean; // sisakan baris badge di atas nama (bila ada UBO di grup)
}
export interface LaidEdge {
  from: string;
  to: string;
  frac: Frac;
  small: boolean;
  sx: number; sy: number;      // titik keluar (bawah sumber)
  ex: number; ey: number;      // titik masuk (atas tujuan)
  lineY: number;               // rel horizontal pertama
  label: string;
  path: string;                // jalur ortogonal lengkap
  color: string;               // warna per pemilik/sumber
  lx: number; ly: number;      // posisi label persen
}

export interface LayoutOpts {
  collapsed: Set<string>;
  showEffective: boolean;
  drawSmall: boolean;      // gambar garis untuk <1%
  showOfficers: boolean;   // tampilkan pengurus & domisili di kartu
}

export const COMPANY_W = 264;
export const PERSON_W = 190;
const RIBBON = 26;
const H_GAP = 46;   // jarak horizontal antar kartu
const V_GAP = 64;   // jarak vertikal antar baris
const NOTE_LH = 17;

export function companyCardHeight(v: Version, opts: LayoutOpts): number {
  let h = RIBBON + 10 /*pad*/ + 20 /*name*/ + 4 + 15 /*modal*/ + 4 + 14 /*kbli*/;
  if (opts.showOfficers) {
    const c = COMPANIES; // noop
    void c;
    h += 6 + v.officers.length * 15 + 4 + 14 /*domisili*/ + 14 /*KPP baris sendiri*/;
  }
  h += 12; // pad bawah
  return h;
}

// menghitung level tiap PT (orang = 0)
function computeLevels(step: number): Record<string, number> {
  const level: Record<string, number> = {};
  const visiting = new Set<string>();
  function lvl(cid: string): number {
    if (level[cid] !== undefined) return level[cid];
    if (visiting.has(cid)) return 1;
    visiting.add(cid);
    const v = activeVersion(cid, step);
    let max = 0;
    if (v) {
      for (const [hid] of v.holders) {
        if (isPerson(hid)) continue;
        const f = directFrac(v, hid);
        if (lessThanPercent(f, 1)) continue; // hanya >=1% menentukan level
        max = Math.max(max, lvl(hid));
      }
    }
    visiting.delete(cid);
    level[cid] = max + 1;
    return level[cid];
  }
  for (const cid of COMPANY_ORDER) if (companyExistsAt(cid, step)) lvl(cid);
  return level;
}

// PT yang tersembunyi karena hanya bisa dicapai lewat kartu yang dilipat
function computeHidden(step: number, collapsed: Set<string>): Set<string> {
  const visible = new Set<string>();
  // mulai dari semua orang (selalu terlihat), telusuri holder -> company
  // bangun peta: holder -> [company...]
  const owns: Record<string, string[]> = {};
  for (const cid of COMPANY_ORDER) {
    const v = activeVersion(cid, step);
    if (!v) continue;
    for (const [hid] of v.holders) {
      (owns[hid] = owns[hid] || []).push(cid);
    }
  }
  const queue: string[] = [...PERSON_ORDER];
  const seen = new Set<string>(PERSON_ORDER);
  while (queue.length) {
    const node = queue.shift()!;
    if (!isPerson(node)) visible.add(node);
    if (collapsed.has(node)) continue; // jangan telusuri keluar dari kartu terlipat
    for (const child of owns[node] || []) {
      if (!seen.has(child)) { seen.add(child); queue.push(child); }
      visible.add(child);
    }
  }
  const hidden = new Set<string>();
  for (const cid of COMPANY_ORDER) {
    if (companyExistsAt(cid, step) && !visible.has(cid)) hidden.add(cid);
  }
  return hidden;
}

export function hasChildren(companyId: string, step: number): boolean {
  for (const cid of COMPANY_ORDER) {
    const v = activeVersion(cid, step);
    if (!v) continue;
    if (v.holders.some((h) => h[0] === companyId)) return true;
  }
  return false;
}

// Warna tetap per pemilik/sumber (orang lalu PT), stabil lintas langkah.
const OWNER_PALETTE = ["#d9822b", "#2f6f8f", "#7d3ca3", "#2e8b57", "#c0392b", "#2c3e8f", "#b8860b", "#0e7c86", "#a03e8f", "#5f6caf"];
export function ownerColor(id: string): string {
  let idx = PERSON_ORDER.indexOf(id);
  if (idx < 0) { const ci = COMPANY_ORDER.indexOf(id); idx = ci < 0 ? 0 : PERSON_ORDER.length + ci; }
  return OWNER_PALETTE[idx % OWNER_PALETTE.length];
}

function coveredX(x: number, iv: Array<[number, number]>): boolean {
  return iv.some(([a, b]) => x >= a - 2 && x <= b + 2);
}
// Cari x pada celah bersih (antar/di luar kotak) yang paling dekat dengan x tujuan.
function nearestGapX(x: number, iv: Array<[number, number]>): number {
  const sorted = [...iv].sort((a, b) => a[0] - b[0]);
  const pad = 12;
  const gaps: Array<[number, number]> = [];
  gaps.push([-Infinity, sorted[0][0] - pad]);
  for (let i = 0; i < sorted.length - 1; i++) gaps.push([sorted[i][1] + pad, sorted[i + 1][0] - pad]);
  gaps.push([sorted[sorted.length - 1][1] + pad, Infinity]);
  let bestX = x, bestD = Infinity;
  for (const [a, b] of gaps) {
    let cx = Math.max(a === -Infinity ? -1e9 : a, Math.min(b === Infinity ? 1e9 : b, x));
    const d = Math.abs(cx - x);
    if (d < bestD) { bestD = d; bestX = cx; }
  }
  return bestX;
}

export function buildLayout(step: number, opts: LayoutOpts, cache: EffCache) {
  const levels = computeLevels(step);
  const hidden = computeHidden(step, opts.collapsed);

  // Kumpulkan node terlihat per level
  const personCaps = PERSON_ORDER.map((id) => personDirectCapital(id, step));
  const personUbo = PERSON_ORDER.map((id) => isUBOCandidate(id, cache));
  const anyUbo = personUbo.some(Boolean);
  const maxParts = Math.max(1, ...personCaps.map((c) => c.parts.length));
  const personH = 64 + (anyUbo ? 18 : 0) + maxParts * 15; // nama + (baris badge) + peran + N baris setoran
  const nodesByLevel: Record<number, LaidNode[]> = {};

  // orang di level 0
  nodesByLevel[0] = PERSON_ORDER.map((id, i) => ({
    id, kind: "person" as const, level: 0, x: 0, y: 0, w: PERSON_W, h: personH, noteH: 0, notes: [],
    capital: personCaps[i].total, holdings: personCaps[i].parts, badgeRow: anyUbo,
  }));

  // perusahaan
  for (const cid of COMPANY_ORDER) {
    if (!companyExistsAt(cid, step) || hidden.has(cid)) continue;
    const v = activeVersion(cid, step)!;
    const lv = levels[cid];
    const notes: NoteLine[] = [];
    const total = totalSharesBig(v);
    // catatan kepemilikan kecil (<1%) bila tidak digambar sebagai garis
    if (!opts.drawSmall) {
      for (const [hid, s] of v.holders) {
        const f = makeFrac(BigInt(s), total);
        if (lessThanPercent(f, 1)) {
          notes.push({ text: `${PERSONS[hid]?.name ?? COMPANIES[hid]?.short ?? hid} ${toPercentString(f)} langsung`, kind: "langsung" });
        }
      }
    }
    if (opts.showEffective) {
      for (const { id, frac } of personsWithEffective(cid, cache)) {
        notes.push({ text: `${PERSONS[id].name} ${toPercentString(frac)} efektif`, kind: "efektif" });
      }
    }
    const h = companyCardHeight(v, opts);
    const noteH = notes.length ? notes.length * NOTE_LH + 6 : 0;
    (nodesByLevel[lv] = nodesByLevel[lv] || []).push({
      id: cid, kind: "company", level: lv, x: 0, y: 0, w: COMPANY_W, h, noteH, notes, version: v,
    });
  }

  // ---------------- Posisi X per level (Y ditunda hingga tinggi rel diketahui) ----------------
  const maxLevel = Math.max(0, ...Object.keys(nodesByLevel).map(Number));
  const rowH: number[] = [];
  for (let lv = 0; lv <= maxLevel; lv++) {
    const arr = nodesByLevel[lv] || [];
    if (lv === 0) arr.sort((a, b) => PERSON_ORDER.indexOf(a.id) - PERSON_ORDER.indexOf(b.id));
    else arr.sort((a, b) => COMPANY_ORDER.indexOf(a.id) - COMPANY_ORDER.indexOf(b.id));
    const totalW = arr.reduce((s, n) => s + n.w, 0) + Math.max(0, arr.length - 1) * H_GAP;
    let x = -totalW / 2;
    rowH[lv] = Math.max(personH, ...arr.map((n) => n.h + n.noteH));
    for (const n of arr) { n.x = x; x += n.w + H_GAP; }
  }

  const allNodes: LaidNode[] = [];
  const nodeMap: Record<string, LaidNode> = {};
  for (let lv = 0; lv <= maxLevel; lv++) for (const n of nodesByLevel[lv] || []) { allNodes.push(n); nodeMap[n.id] = n; }

  // ---------------- Spesifikasi edge (koordinat X dulu; Y & rel dihitung setelahnya) ----------------
  interface Spec {
    from: string; to: string; frac: Frac; small: boolean;
    src: LaidNode; target: LaidNode;
    ex: number; sx: number;
    band1: number; band2: number | null; laneX: number | null;
    lane1: number; lane2: number;
  }
  const specs: Spec[] = [];

  for (const cid of COMPANY_ORDER) {
    const target = nodeMap[cid];
    if (!target || target.kind !== "company") continue;
    const v = target.version!;
    const total = totalSharesBig(v);
    const incoming: { from: string; frac: Frac; small: boolean }[] = [];
    const byHolder = new Map<string, number>();
    for (const [hid, s] of v.holders) byHolder.set(hid, (byHolder.get(hid) || 0) + s);
    byHolder.forEach((s, hid) => {
      const src = nodeMap[hid];
      if (!src) return; // sumber tersembunyi / tidak ada
      const f = makeFrac(BigInt(s), total);
      const small = lessThanPercent(f, 1);
      if (small && !opts.drawSmall) return; // tampil sebagai catatan
      incoming.push({ from: hid, frac: f, small });
    });
    incoming.sort((a, b) => (nodeMap[a.from].x) - (nodeMap[b.from].x));
    const k = incoming.length;
    incoming.forEach((e, i) => {
      const src = nodeMap[e.from];
      const ex = target.x + (target.w * (i + 1)) / (k + 1);
      const iv: Array<[number, number]> = [];
      for (const n of allNodes) if (n.level > src.level && n.level < target.level) iv.push([n.x, n.x + n.w]);
      let band2: number | null = null;
      let laneX: number | null = null;
      if (iv.length && coveredX(ex, iv)) { laneX = nearestGapX(ex, iv); band2 = target.level - 1; }
      specs.push({ from: e.from, to: cid, frac: e.frac, small: e.small, src, target, ex, sx: 0, band1: src.level, band2, laneX, lane1: 0, lane2: 0 });
    });
  }

  // Sebarkan titik KELUAR di sisi bawah tiap sumber agar garis dari pemilik yang sama tak menumpuk.
  const bySource = new Map<string, Spec[]>();
  for (const s of specs) { const a = bySource.get(s.from); if (a) a.push(s); else bySource.set(s.from, [s]); }
  bySource.forEach((list) => {
    list.sort((a, b) => (a.laneX ?? a.ex) - (b.laneX ?? b.ex));
    const m = list.length;
    list.forEach((s, j) => { s.sx = s.src.x + (s.src.w * (j + 1)) / (m + 1); });
  });

  // Alokasi rel horizontal per band via PENGEPAKAN interval: rel yang rentang-x-nya
  // tidak beririsan boleh berbagi baris (lane) yang sama → jumlah rel jauh lebih sedikit,
  // band lebih pendek, garis tidak menumpuk.
  interface Seg { spec: Spec; which: 1 | 2; }
  const segsByBand = new Map<number, Seg[]>();
  const addSeg = (band: number, seg: Seg) => { const a = segsByBand.get(band); if (a) a.push(seg); else segsByBand.set(band, [seg]); };
  for (const s of specs) {
    addSeg(s.band1, { spec: s, which: 1 });
    if (s.band2 !== null) addSeg(s.band2, { spec: s, which: 2 });
  }
  const segSpan = (seg: Seg): [number, number] => {
    const s = seg.spec;
    const xa = seg.which === 1 ? s.sx : s.laneX!;
    const xb = seg.which === 1 ? (s.band2 !== null ? s.laneX! : s.ex) : s.ex;
    return xa <= xb ? [xa, xb] : [xb, xa];
  };
  const PACK_PAD = 26;
  const lanesInBand: number[] = [];
  segsByBand.forEach((segs, band) => {
    const items = segs.map((seg) => ({ seg, span: segSpan(seg) }));
    items.sort((a, b) => a.span[0] - b.span[0]);
    const laneEnds: number[] = [];
    for (const it of items) {
      let lane = -1;
      for (let i = 0; i < laneEnds.length; i++) {
        if (it.span[0] > laneEnds[i] + PACK_PAD) { lane = i; break; }
      }
      if (lane === -1) { lane = laneEnds.length; laneEnds.push(it.span[1]); }
      else laneEnds[lane] = Math.max(laneEnds[lane], it.span[1]);
      if (it.seg.which === 1) it.seg.spec.lane1 = lane; else it.seg.spec.lane2 = lane;
    }
    lanesInBand[band] = laneEnds.length;
  });

  // ---------------- Tinggi tiap band dinamis (sesuai jumlah rel) & posisi Y ----------------
  const LANE_SP = 24, GAP_PAD = 34, GAP_MIN = V_GAP;
  const gapBelow: number[] = [];
  for (let lv = 0; lv <= maxLevel; lv++) {
    const lanes = lanesInBand[lv] || 0;
    gapBelow[lv] = Math.max(GAP_MIN, lanes * LANE_SP + GAP_PAD);
  }
  const levelY: number[] = [];
  {
    let yy = 0;
    for (let lv = 0; lv <= maxLevel; lv++) { levelY[lv] = yy; yy += rowH[lv] + gapBelow[lv]; }
  }
  for (const n of allNodes) n.y = levelY[n.level];

  const levelBottom = (lv: number) => levelY[lv] + (rowH[lv] || 0);
  const laneY = (band: number, idx: number) => {
    const count = lanesInBand[band] || 1;
    return levelBottom(band) + (gapBelow[band] * (idx + 1)) / (count + 1);
  };

  // ---------------- Bangun edges (jalur ortogonal, sudut membulat) ----------------
  const CORNER_R = 12;
  const roundPath = (raw: { x: number; y: number }[]): string => {
    // buang titik kembar berurutan
    const p = raw.filter((pt, i) => i === 0 || pt.x !== raw[i - 1].x || pt.y !== raw[i - 1].y);
    if (p.length < 2) return "";
    let d = `M ${p[0].x} ${p[0].y}`;
    for (let i = 1; i < p.length - 1; i++) {
      const a = p[i - 1], b = p[i], c = p[i + 1];
      const v1x = b.x - a.x, v1y = b.y - a.y;
      const v2x = c.x - b.x, v2y = c.y - b.y;
      const l1 = Math.hypot(v1x, v1y), l2 = Math.hypot(v2x, v2y);
      if (l1 === 0 || l2 === 0) { d += ` L ${b.x} ${b.y}`; continue; }
      const rr = Math.min(CORNER_R, l1 / 2, l2 / 2);
      const bx = b.x - (v1x / l1) * rr, by = b.y - (v1y / l1) * rr;
      const ax = b.x + (v2x / l2) * rr, ay = b.y + (v2y / l2) * rr;
      d += ` L ${bx} ${by} Q ${b.x} ${b.y} ${ax} ${ay}`;
    }
    const last = p[p.length - 1];
    d += ` L ${last.x} ${last.y}`;
    return d;
  };

  interface EdgeGeom {
    from: string; to: string; frac: Frac; small: boolean;
    sx: number; sy: number; ex: number; ey: number; trackY1: number;
    d: string; hsegs: { y: number; x0: number; x1: number }[];
  }
  const geoms: EdgeGeom[] = [];
  const verticals: { x: number; y0: number; y1: number }[] = [];

  for (const s of specs) {
    const sy = s.src.y + s.src.h;
    const ey = s.target.y;
    const trackY1 = laneY(s.band1, s.lane1);
    let pts: { x: number; y: number }[];
    if (s.band2 !== null && s.laneX !== null) {
      const trackY2 = laneY(s.band2, s.lane2);
      pts = [
        { x: s.sx, y: sy }, { x: s.sx, y: trackY1 }, { x: s.laneX, y: trackY1 },
        { x: s.laneX, y: trackY2 }, { x: s.ex, y: trackY2 }, { x: s.ex, y: ey - 7 },
      ];
    } else {
      pts = [{ x: s.sx, y: sy }, { x: s.sx, y: trackY1 }, { x: s.ex, y: trackY1 }, { x: s.ex, y: ey - 7 }];
    }
    // pisahkan segmen vertikal (untuk deteksi persilangan) & horizontal (untuk taruh label)
    const hsegs: { y: number; x0: number; x1: number }[] = [];
    for (let i = 0; i < pts.length - 1; i++) {
      const a = pts[i], b = pts[i + 1];
      if (a.x === b.x && a.y !== b.y) verticals.push({ x: a.x, y0: Math.min(a.y, b.y), y1: Math.max(a.y, b.y) });
      else if (a.y === b.y && a.x !== b.x) hsegs.push({ y: a.y, x0: Math.min(a.x, b.x), x1: Math.max(a.x, b.x) });
    }
    geoms.push({ from: s.from, to: s.to, frac: s.frac, small: s.small, sx: s.sx, sy, ex: s.ex, ey, trackY1, d: roundPath(pts), hsegs });
  }

  // Tempatkan label persen pada TITIK BERSIH di rel horizontal (jauh dari garis vertikal yang memotong).
  const edges: LaidEdge[] = geoms.map((g) => {
    const gLabel = toPercentString(g.frac);
    // pilih rel horizontal terpanjang milik edge ini
    const rail = g.hsegs.length
      ? g.hsegs.reduce((a, b) => (b.x1 - b.x0 > a.x1 - a.x0 ? b : a))
      : { y: g.trackY1, x0: Math.min(g.sx, g.ex), x1: Math.max(g.sx, g.ex) };
    const pillW = gLabel.length * 7 + 12;
    const half = pillW / 2;
    const lo = rail.x0 + half + 4, hi = rail.x1 - half - 4;
    let lx: number;
    if (hi <= lo) {
      lx = (rail.x0 + rail.x1) / 2; // rel terlalu pendek → tengah saja
    } else {
      // x garis vertikal (edge lain) yang memotong rel ini
      const crossings: number[] = [];
      for (const v of verticals) {
        if (v.y0 - 3 <= rail.y && rail.y <= v.y1 + 3 && v.x >= rail.x0 - 40 && v.x <= rail.x1 + 40) crossings.push(v.x);
      }
      if (crossings.length === 0) {
        lx = (rail.x0 + rail.x1) / 2;
      } else {
        // kandidat: titik-tengah antar persilangan + kedua ujung; pilih yang jarak ke persilangan terdekat paling besar
        crossings.sort((a, b) => a - b);
        const cands = [lo, hi];
        for (let i = 0; i < crossings.length - 1; i++) cands.push((crossings[i] + crossings[i + 1]) / 2);
        cands.push((rail.x0 + rail.x1) / 2);
        let bestX = (rail.x0 + rail.x1) / 2, bestD = -1;
        for (let cx of cands) {
          cx = Math.max(lo, Math.min(hi, cx));
          let dmin = Infinity;
          for (const c of crossings) dmin = Math.min(dmin, Math.abs(cx - c));
          if (dmin > bestD) { bestD = dmin; bestX = cx; }
        }
        lx = bestX;
      }
    }
    return {
      from: g.from, to: g.to, frac: g.frac, small: g.small,
      sx: g.sx, sy: g.sy, ex: g.ex, ey: g.ey, lineY: g.trackY1,
      label: gLabel, path: g.d, color: ownerColor(g.from), lx, ly: rail.y,
    };
  });

  // bounding box
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const n of allNodes) {
    minX = Math.min(minX, n.x);
    minY = Math.min(minY, n.y);
    maxX = Math.max(maxX, n.x + n.w);
    maxY = Math.max(maxY, n.y + n.h + n.noteH);
  }
  if (!isFinite(minX)) { minX = 0; minY = 0; maxX = 100; maxY = 100; }

  return { nodes: allNodes, nodeMap, edges, bbox: { minX, minY, maxX, maxY } };
}

export type Layout = ReturnType<typeof buildLayout>;
